import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Sparkles, Crown, Star } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

/**
 * PricingLadder — homepage pricing ladder shown immediately below Hero.
 * 4 tiers with clear value ladder + "Most Popular" anchor on ₹299.
 *
 * Design intent (per user request Feb 17, 2026):
 *  - Compact, ~1 screen high, scannable in <10 seconds
 *  - Gold/Navy premium theme matching site palette
 *  - Each card has price, primary benefit line, 3 features, and a CTA
 *  - CTA routes to /booking?tier=<slug> for the higher tiers
 *  - ₹99 tier CTAs go directly to /poocho-guruji (fastest revenue path)
 *  - Fires a GA4 event (`pricing_ladder_cta_click`) with tier + slot for CRO analysis
 */

const TIERS = [
  {
    slug: 'quick',
    price: 99,
    label: 'Poocho Guruji Quick',
    labelHi: 'पूछो गुरुजी · Quick',
    subtitle: '1 प्रश्न · Personal Vedic Answer',
    subtitleHi: 'एक प्रश्न का व्यक्तिगत वैदिक उत्तर',
    features: [
      'व्यक्तिगत वैदिक विश्लेषण',
      'WhatsApp पर उत्तर',
      'लगभग 10 मिनट में उत्तर',
    ],
    ctaText: 'अभी पूछो',
    ctaHref: '/poocho-guruji#poocho-question-picker',
    badge: null,
    accent: 'gold',
    icon: MessageCircle,
  },
  {
    slug: 'deep_299',
    price: 299,
    label: 'Deep Analysis',
    labelHi: 'गहन विश्लेषण',
    subtitle: 'Multi-question deep dive',
    subtitleHi: '3 प्रश्न + Dasha विश्लेषण',
    features: [
      '3 प्रश्नों का उत्तर',
      'Dasha/Antardasha timing',
      'PDF रिपोर्ट + WhatsApp',
    ],
    ctaText: 'Book ₹299',
    ctaHref: '/booking?tier=deep_299',
    badge: 'Most Popular',
    accent: 'gold-solid',
    icon: Sparkles,
  },
  {
    slug: 'premium_999',
    price: 999,
    label: 'Poocho Guruji Premium',
    labelHi: 'पूछो गुरुजी · Premium',
    subtitle: '30-day Kundli-based AI Guidance',
    subtitleHi: '30 दिन का व्यक्तिगत AI मार्गदर्शन',
    features: [
      '30 दिन प्रतिदिन 1 वैदिक मार्गदर्शन',
      'Dasha, Gochar + Kundli आधारित उत्तर',
      'Kundli, प्रश्न, उत्तर History सुरक्षित',
    ],
    ctaText: 'Book ₹999',
    ctaHref: '/booking?tier=premium_999',
    badge: 'Best Value',
    accent: 'gold',
    icon: Star,
  },
  {
    slug: 'personal_5100',
    price: 5100,
    label: 'Personal Consultation',
    labelHi: 'व्यक्तिगत परामर्श',
    subtitle: '90-min in-depth session',
    subtitleHi: '90 मिनट गहन परामर्श',
    features: [
      '90-मिनट In-person/Video',
      'Complete life reading',
      'Custom Yantra + Remedies',
    ],
    ctaText: 'Book ₹5,100',
    ctaHref: '/booking?tier=personal_5100',
    badge: null,
    accent: 'royal',
    icon: Crown,
  },
];

const trackClick = (tier, slotIndex) => {
  try {
    trackEvent('pricing_ladder_cta_click', {
      tier,
      price_inr: TIERS.find((t) => t.slug === tier)?.price || 0,
      slot: slotIndex,
      source: 'homepage_hero_ladder',
    });
  } catch (_) { /* never break UI */ }
};

const cardStyle = (accent, isPopular) => {
  const base = 'relative flex flex-col rounded-2xl p-6 pt-8 border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group h-full';
  if (isPopular) {
    return `${base} bg-gradient-to-br from-[#1a1230] via-[#0f0b25] to-[#0a0820] border-gold shadow-[0_0_40px_rgba(212,175,55,0.25)]`;
  }
  if (accent === 'royal') {
    return `${base} bg-gradient-to-br from-[#0f0b25] to-[#050418] border-gold/60`;
  }
  return `${base} bg-[#0a0820]/70 border-gold/25 hover:border-gold/60`;
};

const priceStyle = (isPopular) =>
  isPopular
    ? 'text-5xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-b from-[#FDE49B] via-[#F4D477] to-[#B8942D] leading-none'
    : 'text-5xl font-heading font-bold text-gold leading-none';

const buttonStyle = (isPopular) =>
  isPopular
    ? 'w-full mt-6 py-3 rounded-lg font-semibold text-sm bg-gradient-to-r from-[#F4D477] via-[#E5C158] to-[#B8942D] text-[#0a0820] hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-gold/20'
    : 'w-full mt-6 py-3 rounded-lg font-semibold text-sm border border-gold/60 text-gold hover:bg-gold hover:text-[#0a0820] active:scale-95 transition-all';

export default function PricingLadder() {
  return (
    <section
      id="pricing-ladder"
      data-testid="pricing-ladder"
      className="relative py-14 md:py-20 bg-gradient-to-b from-[#020410] via-[#050820] to-[#030612] overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gold/5 blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 max-w-7xl">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-12">
          <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold font-body mb-3">
            Choose Your Path · मार्ग चुनें
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-text-main mb-3 tracking-tight">
            हर बजट के लिए एक <span className="text-gold">Vedic Solution</span>
          </h2>
          <p className="text-text-muted text-sm md:text-base max-w-2xl mx-auto">
            33+ वर्ष का अनुभव · 4.9★ Rating · 33,000+ Happy Clients · Secure Razorpay Payments
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {TIERS.map((tier, i) => {
            const Icon = tier.icon;
            const isPopular = tier.badge === 'Most Popular';
            return (
              <div
                key={tier.slug}
                data-testid={`pricing-tier-${tier.slug}`}
                className={cardStyle(tier.accent, isPopular)}
              >
                {/* Badge */}
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold tracking-widest uppercase ${
                        isPopular
                          ? 'bg-gradient-to-r from-[#F4D477] to-[#B8942D] text-[#0a0820] shadow-lg shadow-gold/40'
                          : 'bg-gold/15 text-gold border border-gold/40'
                      }`}
                    >
                      {tier.badge}
                    </span>
                  </div>
                )}

                {/* Icon + Label */}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                      isPopular ? 'bg-gold/20' : 'bg-gold/10'
                    }`}
                  >
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <div className="font-heading text-lg text-text-main leading-tight">{tier.label}</div>
                    <div className="text-xs text-gold/70">{tier.labelHi}</div>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-2 flex items-baseline gap-2">
                  <span className={priceStyle(isPopular)}>
                    ₹{tier.price.toLocaleString('en-IN')}
                  </span>
                </div>

                {/* Subtitle */}
                <div className="text-xs text-text-muted mb-1">{tier.subtitle}</div>
                <div className="text-sm text-text-main mb-4">{tier.subtitleHi}</div>

                {/* Features */}
                <ul className="space-y-2 mb-2 flex-grow">
                  {tier.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-text-main/90">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gold flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={tier.ctaHref}
                  onClick={() => trackClick(tier.slug, i)}
                  className={buttonStyle(isPopular)}
                  data-testid={`pricing-cta-${tier.slug}`}
                >
                  {tier.ctaText} →
                </Link>
              </div>
            );
          })}
        </div>

        {/* Reassurance strip */}
        <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-xs md:text-sm text-text-muted">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            100% Secure Razorpay Payment
          </span>
          <span className="hidden md:inline text-gold/40">·</span>
          <span>4.9★ Rating (287+ Reviews)</span>
          <span className="hidden md:inline text-gold/40">·</span>
          <span>WhatsApp सहायता · +91 93113 69729</span>
        </div>
      </div>
    </section>
  );
}
